import React, { useState, useRef, useEffect } from 'react';
import { useRBAC } from '../../contexts/RBACContext';
import { useProfile } from '../../contexts/ProfileContext';
import { BRAND_GREEN, BRAND_GOLD } from '../../theme/sucronTheme';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const ROLE_LABELS = {
  pt: {
    profile:          'Perfil',
    ATLETA:           'Atleta',
    TREINADOR:        'Treinador',
    PREPARADOR_FISICO:'Preparador Físico',
    GESTOR:           'Gestor',
    NUTRICIONISTA:    'Nutricionista',
    BIOMECANICO:      'Biomecânico',
  },
  en: {
    profile:          'Profile',
    ATLETA:           'Athlete',
    TREINADOR:        'Coach',
    PREPARADOR_FISICO:'S&C Coach',
    GESTOR:           'Manager',
    NUTRICIONISTA:    'Nutritionist',
    BIOMECANICO:      'Biomechanics Specialist',
  },
};

/**
 * RoleSwitcher -- Selects active profile (role).
 *
 * Admin: shows all 6 profiles (can preview any role's sidebar).
 * Regular user: shows current role as static indicator (no dropdown).
 * Single-role user: shows role as static indicator (no dropdown).
 *
 * When role changes, navConfig re-filters sections automatically
 * via getVisibleNavSections({ roleKey }).
 */
const PROFILE_KEYS = [
  { key: 'ATLETA',           icon: '🏃' },
  { key: 'TREINADOR',        icon: '📋' },
  { key: 'PREPARADOR_FISICO',icon: '💪' },
  { key: 'GESTOR',           icon: '⚙️' },
  { key: 'NUTRICIONISTA',    icon: '🥗' },
  { key: 'BIOMECANICO',      icon: '🦴' },
  // RECRUITER — Fase 2
];

const RoleSwitcher = () => {
  const { activeRole, switchRole, roles } = useRBAC();
  const { isSuperAdmin } = useProfile();
  const { lang } = useGovernanceLang();
  const t = ROLE_LABELS[lang] || ROLE_LABELS.pt;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Build profiles with bilingual labels
  const PROFILES = PROFILE_KEYS.map(p => ({ ...p, label: t[p.key] || p.key }));

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Determine which profiles are available
  // Admin: all profiles (preview any role). Regular: ONLY their token roles.
  // activeRole alone NEVER adds a profile — prevents privilege escalation.
  const availableProfiles = isSuperAdmin
    ? PROFILES
    : PROFILES.filter((p) => roles.includes(p.key));

  const canSwitch = isSuperAdmin || availableProfiles.length > 1;

  // Single-role non-admin: hide entirely (no selector needed)
  if (!canSwitch) return null;

  const activeProfile = PROFILES.find(p => p.key === activeRole) || availableProfiles[0];

  return (
    <div ref={ref} className="relative px-3 py-2 border-b border-gray-100">
      <button
        onClick={() => canSwitch && setOpen(!open)}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 ${
          canSwitch ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
        }`}
      >
        <span className="text-lg">{activeProfile.icon}</span>
        <div className="flex-1 text-left">
          <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: BRAND_GOLD }}>
            {t.profile}
          </p>
          <p className="text-sm font-semibold" style={{ color: BRAND_GREEN }}>
            {activeProfile.label}
          </p>
        </div>
        {canSwitch && (
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {open && (
        <div className="absolute left-3 right-3 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          {availableProfiles.map((profile) => {
            const isActive = profile.key === activeRole;
            return (
              <button
                key={profile.key}
                onClick={() => { switchRole(profile.key); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 ${
                  isActive ? 'bg-green-50 border-l-4' : 'hover:bg-gray-50 border-l-4 border-transparent'
                }`}
                style={isActive ? { borderLeftColor: BRAND_GOLD } : {}}
              >
                <span className="text-lg">{profile.icon}</span>
                <p className={`text-sm ${isActive ? 'font-bold' : 'font-medium'}`} style={{ color: BRAND_GREEN }}>
                  {profile.label}
                </p>
                {isActive && (
                  <svg className="w-4 h-4 ml-auto" style={{ color: BRAND_GREEN }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RoleSwitcher;
